import React, { useMemo, useEffect, useRef, useState } from "react";

/* ------------------------------ tiny helpers ------------------------------ */
function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function normalizeOverlap(o) {
  if (o === "–" || o === "—") return "-";
  return o || "-";
}
function overlapLabel(o) {
  const x = normalizeOverlap(o);
  if (x === "+") return "Easy (close match)";
  if (x === "~") return "Medium (partial overlap)";
  return "Hard (no analogy)";
}
function overlapTileClass(o) {
  const x = normalizeOverlap(o);
  if (x === "+") return "bg-green-100 text-green-900 border-green-200 hover:bg-green-200";
  if (x === "~") return "bg-yellow-100 text-yellow-900 border-yellow-200 hover:bg-yellow-200";
  return "bg-red-100 text-red-900 border-red-200 hover:bg-red-200";
}
function overlapSelectedRingClass(o) {
  const x = normalizeOverlap(o);
  if (x === "+") return "ring-green-500";
  if (x === "~") return "ring-yellow-500";
  return "ring-red-500";
}

// Chart-sized button (no slashes) for IPA tables
function ChartSymbol({ ipa, overlap, selected, onClick }) {
  const x = normalizeOverlap(overlap);
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "inline-flex min-w-[34px] items-center justify-center rounded-lg border px-2 py-1 text-sm font-semibold leading-none transition active:scale-[0.99]",
        overlapTileClass(x),
        selected ? cx("ring-2 ring-offset-2", overlapSelectedRingClass(x)) : "opacity-90 hover:opacity-100"
      )}
      title={overlapLabel(x)}
    >
      {ipa}
    </button>
  );
}

/* ============================= CONSONANT CHART ============================= */
/**
 * NTNU L1–L2map-inspired layout:
 * - Place headers grouped: Labial / Coronal / Dorsal / Radical / Laryngeal
 * - Remove columns not used (auto)
 * - Remove manner rows not used (auto)
 */

const CONSONANT_PLACE_GROUPS = [
  { group: "Labial", places: ["Bilabial", "Labio-dental"] },
  { group: "Coronal", places: ["Dental", "Alveolar", "Palato-alveolar", "Retroflex"] },
  { group: "Dorsal", places: ["Palatal", "Velar", "Uvular"] },
  { group: "Radical", places: ["Pharyngeal", "Epi-glottal"] },
  { group: "Laryngeal", places: ["Glottal"] },
];

const CONSONANT_MANNERS = [
  { key: "plosive", label: "Plosive" },
  { key: "affricate", label: "Affricates" },
  { key: "nasal", label: "Nasal" },
  { key: "trill", label: "Trill" },
  { key: "tap", label: "Tap, Flap" },
  { key: "lat_flap", label: "Lateral flap" },
  { key: "fricative", label: "Fricative" },
  { key: "sib_fricative", label: "Sibilant fricatives" },
  { key: "lat_fricative", label: "Lateral fricative" },
  { key: "approximant", label: "Approximant" },
  { key: "lat_approximant", label: "Lateral approximant" },
];

/**
 * Norwegian consonant placement. Includes retroflex lateral /ɭ/.
 */
const CONSONANT_POS = {
  // plosives
  p: ["plosive", "Bilabial"],
  b: ["plosive", "Bilabial"],
  t: ["plosive", "Alveolar"],
  d: ["plosive", "Alveolar"],
  k: ["plosive", "Velar"],
  g: ["plosive", "Velar"],
  "ɡ": ["plosive", "Velar"],
  "ʈ": ["plosive", "Retroflex"],
  "ɖ": ["plosive", "Retroflex"],

  // affricates (if present)
  "t͡s": ["affricate", "Alveolar"],
  "t͡ʃ": ["affricate", "Palato-alveolar"],

  // nasals
  m: ["nasal", "Bilabial"],
  n: ["nasal", "Alveolar"],
  "ŋ": ["nasal", "Velar"],
  "ɲ": ["nasal", "Palatal"],
  "ɳ": ["nasal", "Retroflex"],

  // trills / taps
  r: ["trill", "Alveolar"],
  "ʀ": ["trill", "Uvular"],
  "ɾ": ["tap", "Alveolar"],
  "ɽ": ["tap", "Retroflex"],

  // fricatives / sibilants
  f: ["fricative", "Labio-dental"],
  v: ["fricative", "Labio-dental"],
  θ: ["fricative", "Dental"],
  ð: ["fricative", "Dental"],

  s: ["sib_fricative", "Alveolar"],
  z: ["sib_fricative", "Alveolar"],
  "ʃ": ["sib_fricative", "Palato-alveolar"],
  "ʒ": ["sib_fricative", "Palato-alveolar"],
  "ʂ": ["sib_fricative", "Retroflex"],
  "ʐ": ["sib_fricative", "Retroflex"],

  ç: ["fricative", "Palatal"],
  ʝ: ["fricative", "Palatal"],
  x: ["fricative", "Velar"],
  ɣ: ["fricative", "Velar"],
  χ: ["fricative", "Uvular"],
  ʁ: ["fricative", "Uvular"],
  h: ["fricative", "Glottal"],
  ɦ: ["fricative", "Glottal"],

  // approximants
  j: ["approximant", "Palatal"],
  "ʋ": ["approximant", "Labio-dental"],
  ɰ: ["approximant", "Velar"],

  // laterals
  l: ["lat_approximant", "Alveolar"],
  ɫ: ["lat_approximant", "Alveolar"],
  ʎ: ["lat_approximant", "Palatal"],
  "ɭ": ["lat_approximant", "Retroflex"],
};

function buildConsonantChart(norConsonants) {
  const placed = new Map(); // manner|place -> [ipa...]
  const usedPlaces = new Set();
  const usedManners = new Set();
  const unplaced = [];

  for (const s of norConsonants) {
    const ipa = s.ipa;
    const pos = CONSONANT_POS[ipa];
    if (!pos) {
      unplaced.push(ipa);
      continue;
    }
    const [manner, place] = pos;
    usedPlaces.add(place);
    usedManners.add(manner);

    const k = `${manner}|${place}`;
    if (!placed.has(k)) placed.set(k, []);
    placed.get(k).push(ipa);
  }

  for (const [k, arr] of placed.entries()) {
    placed.set(k, [...new Set(arr)].sort((a, b) => a.localeCompare(b)));
  }

  return {
    placed,
    unplaced: [...new Set(unplaced)].sort((a, b) => a.localeCompare(b)),
    usedPlaces,
    usedManners,
  };
}

function computeVisibleConsonantGroups(usedPlaces) {
  const groups = [];
  for (const g of CONSONANT_PLACE_GROUPS) {
    const places = g.places.filter((p) => usedPlaces.has(p));
    if (places.length) groups.push({ group: g.group, places });
  }
  return groups;
}

export function ConsonantChart({ inventory, getOverlap, selectedIpa, onSelect }) {
  const { placed, unplaced, usedPlaces, usedManners } = useMemo(() => buildConsonantChart(inventory), [inventory]);

  const visibleGroups = useMemo(() => computeVisibleConsonantGroups(usedPlaces), [usedPlaces]);
  const visiblePlaces = useMemo(() => visibleGroups.flatMap((g) => g.places), [visibleGroups]);
  const visibleManners = useMemo(() => CONSONANT_MANNERS.filter((m) => usedManners.has(m.key)), [usedManners]);

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[880px] rounded-2xl border bg-white">
        <div
          className="grid"
          style={{
            gridTemplateColumns: `190px repeat(${visiblePlaces.length}, minmax(70px, 1fr))`,
          }}
        >
          {/* Group header row */}
          <div className="border-b bg-slate-50 px-3 py-2" />
          {visibleGroups.map((g) => (
            <div
              key={g.group}
              className="border-b border-l bg-slate-50 px-2 py-2 text-center text-xs font-semibold text-slate-700"
              style={{ gridColumn: `span ${g.places.length}` }}
            >
              {g.group}
            </div>
          ))}

          {/* Place header row */}
          <div className="border-b bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700" />
          {visiblePlaces.map((p) => (
            <div
              key={p}
              className="border-b border-l bg-slate-50 px-2 py-2 text-center text-[11px] font-semibold text-slate-700"
            >
              {p}
            </div>
          ))}

          {/* Body */}
          {visibleManners.map((row) => (
            <React.Fragment key={row.key}>
              <div className="border-b bg-white px-3 py-2 text-xs font-semibold text-slate-700">{row.label}</div>

              {visiblePlaces.map((place) => {
                const key = `${row.key}|${place}`;
                const symbols = placed.get(key) || [];
                return (
                  <div key={key} className="border-b border-l bg-white px-2 py-2">
                    <div className="flex flex-wrap gap-1">
                      {symbols.map((ipa) => (
                        <ChartSymbol
                          key={ipa}
                          ipa={ipa}
                          overlap={getOverlap(ipa)}
                          selected={selectedIpa === ipa}
                          onClick={() => onSelect(ipa)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {unplaced.length ? (
        <div className="mt-3 rounded-2xl border bg-slate-50 p-4">
          <div className="text-xs font-semibold text-slate-700">Unplaced (still Norwegian)</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {unplaced.map((ipa) => (
              <ChartSymbol
                key={ipa}
                ipa={ipa}
                overlap={getOverlap(ipa)}
                selected={selectedIpa === ipa}
                onClick={() => onSelect(ipa)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* =============================== VOWEL CHART =============================== */
// Map base vowel (no length marks) -> [row, col]
const VOWEL_POS = {
  // Close
  i: ["Close", "Front"],
  y: ["Close", "Front"],
  "ʉ": ["Close", "Central"],
  u: ["Close", "Back"],

  // Near-close
  ɪ: ["Near-close", "Near-front"],
  ʏ: ["Near-close", "Near-front"],
  ʊ: ["Near-close", "Near-back"],

  // Close-mid
  e: ["Close-mid", "Front"],
  ø: ["Close-mid", "Front"],
  "ɵ": ["Close-mid", "Central"],
  o: ["Close-mid", "Back"],

  // Mid
  ə: ["Mid", "Central"],

  // Open-mid
  ɛ: ["Open-mid", "Front"],
  œ: ["Open-mid", "Front"],
  "ɜ": ["Open-mid", "Central"],
  "ʌ": ["Open-mid", "Near-back"],
  ɔ: ["Open-mid", "Back"],

  // Near-open
  æ: ["Near-open", "Front"],
  "ɐ": ["Near-open", "Central"],

  // Open
  a: ["Open", "Central"],
  "ɑ": ["Open", "Back"],
  "ä": ["Open", "Central"],
};

const VOWEL_ROWS = ["Close", "Near-close", "Close-mid", "Mid", "Open-mid", "Near-open", "Open"];
const VOWEL_COLS = ["Front", "Near-front", "Central", "Near-back", "Back"];

// Fine offsets *inside the cell* to imitate NTNU-like micro placement.
const VOWEL_OFFSET = {
  i: { dx: -0.26, dy: 0.0 },
  y: { dx: -0.06, dy: 0.0 },
  "ʉ": { dx: 0.08, dy: 0.0 },
  u: { dx: -0.10, dy: 0.0 },

  ɪ: { dx: -0.06, dy: 0.0 },
  ʏ: { dx: 0.10, dy: 0.0 },
  ʊ: { dx: -0.08, dy: 0.0 },

  e: { dx: -0.14, dy: 0.0 },
  ø: { dx: 0.06, dy: 0.0 },

  "ɵ": { dx: 0.06, dy: 0.0 },
  o: { dx: -0.06, dy: 0.0 },

  ɛ: { dx: -0.10, dy: 0.0 },
  œ: { dx: 0.08, dy: 0.0 },

  "ɜ": { dx: 0.0, dy: 0.0 },
  "ʌ": { dx: 0.06, dy: 0.0 },
  ɔ: { dx: -0.06, dy: 0.0 },

  æ: { dx: -0.02, dy: 0.0 },

  a: { dx: -0.12, dy: 0.02 },
  "ä": { dx: -0.04, dy: 0.02 },
  "ɑ": { dx: -0.08, dy: -0.02 },

  ə: { dx: 0.02, dy: 0.0 },
  "ɐ": { dx: 0.02, dy: 0.0 },
};

function stripLengthMarks(ipa) {
  return String(ipa || "").replace(/ː|:/g, "");
}
function isLong(ipa) {
  return /ː|:/.test(String(ipa || ""));
}

function useMeasureWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);

  useEffect(() => {
    if (!ref.current) return;

    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const next = Math.max(0, Math.floor(entry.contentRect.width));
      setW(next);
    });

    ro.observe(el);
    setW(Math.max(0, Math.floor(el.getBoundingClientRect().width)));

    return () => ro.disconnect();
  }, []);

  return [ref, w];
}

function offsetToPx({ dx, dy }, W, H) {
  const sx = W * 0.06;
  const sy = H * 0.045;
  return { ox: dx * sx, oy: dy * sy };
}

export function VowelChart({ inventory, getOverlap, selectedIpa, onSelect }) {
  const [wrapRef, width] = useMeasureWidth();

  const W = Math.max(520, Math.min(980, width || 820));
  const H = Math.round(W * 0.62);

  const PAD = {
    left: Math.round(W * 0.17),
    right: Math.round(W * 0.05),
    top: Math.round(H * 0.16),
    bottom: Math.round(H * 0.12),
  };

  function pos(ri, ci) {
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;

    const yNorm = ri / (VOWEL_ROWS.length - 1);
    const xNorm = ci / (VOWEL_COLS.length - 1);

    const leftTop = PAD.left + innerW * 0.18;
    const rightTop = PAD.left + innerW * 0.92;
    const leftBottom = PAD.left + innerW * 0.38;
    const rightBottom = PAD.left + innerW * 0.99;

    const leftX = leftTop + (leftBottom - leftTop) * yNorm;
    const rightX = rightTop + (rightBottom - rightTop) * yNorm;

    const bias =
      ci === 0
        ? Math.max(6, Math.round(W * 0.01))
        : ci === 4
        ? -Math.max(6, Math.round(W * 0.01))
        : 0;

    return {
      x: leftX + (rightX - leftX) * xNorm + bias,
      y: PAD.top + innerH * yNorm,
    };
  }

  const grouped = useMemo(() => {
    const bins = {};
    for (const v of inventory) {
      const ipa = v.ipa;
      const base = stripLengthMarks(ipa);
      const mapping = VOWEL_POS[base];
      if (!mapping) continue;

      const [row, col] = mapping;
      const key = `${row}|${col}`;
      if (!bins[key]) bins[key] = [];
      bins[key].push(ipa);
    }

    for (const k of Object.keys(bins)) {
      bins[k] = [...new Set(bins[k])].sort((a, b) => {
        const la = isLong(a);
        const lb = isLong(b);
        if (la !== lb) return la ? 1 : -1;
        return String(a).localeCompare(String(b));
      });
    }
    return bins;
  }, [inventory]);

  return (
    <div ref={wrapRef} className="w-full overflow-hidden">
      <div className="rounded-2xl border bg-white p-4">
        <div className="relative mx-auto" style={{ width: W, height: H }}>
          <svg width={W} height={H} className="absolute inset-0">
            {[
              pos(0, 0),
              pos(0, 2),
              pos(0, 4),
              pos(2, 1),
              pos(4, 1),
              pos(6, 2),
              pos(6, 4),
            ].map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r={Math.max(5, Math.round(W * 0.007))}
                fill="#111"
              />
            ))}

            {/* outline */}
            {(() => {
              const tl = pos(0, 0);
              const tr = pos(0, 4);
              const bl = pos(6, 0);
              const br = pos(6, 4);
              return (
                <path
                  d={`M ${tl.x} ${tl.y} L ${tr.x} ${tr.y} L ${br.x} ${br.y} L ${bl.x} ${bl.y} Z`}
                  fill="none"
                  stroke="#1f2937"
                  strokeWidth="2"
                />
              );
            })()}

            {/* guide lines */}
            {[2, 4].map((ri) => {
              const a = pos(ri, 0);
              const b = pos(ri, 4);
              return (
                <line
                  key={ri}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke="#6b7280"
                  strokeWidth="1.5"
                />
              );
            })}

            {/* central vertical */}
            {(() => {
              const a = pos(0, 2);
              const b = pos(6, 2);
              return (
                <line
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke="#6b7280"
                  strokeWidth="1.5"
                />
              );
            })()}
          </svg>

          {/* column labels */}
          {VOWEL_COLS.map((c, ci) => {
            const p = pos(0, ci);
            return (
              <div
                key={c}
                className="absolute -translate-x-1/2 text-sm font-semibold text-slate-800"
                style={{ left: p.x, top: Math.max(8, Math.round(H * 0.04)) }}
              >
                {c}
              </div>
            );
          })}

          {/* row labels */}
          {VOWEL_ROWS.map((r, ri) => {
            const p = pos(ri, 0);
            return (
              <div
                key={r}
                className="absolute -translate-y-1/2 text-sm font-semibold text-slate-800"
                style={{ left: Math.max(8, Math.round(W * 0.02)), top: p.y }}
              >
                {r}
              </div>
            );
          })}

          {/* vowel symbols */}
          {Object.entries(grouped).map(([cellKey, items]) => {
            const [row, col] = cellKey.split("|");
            const ri = VOWEL_ROWS.indexOf(row);
            const ci = VOWEL_COLS.indexOf(col);
            if (ri < 0 || ci < 0) return null;

            const basePoint = pos(ri, ci);
            const n = items.length;
            const spread = Math.min(18, Math.round(W * 0.018));
            const start = -(n - 1) / 2;

            return (
              <div key={cellKey} className="absolute" style={{ left: basePoint.x, top: basePoint.y }}>
                <div className="relative">
                  {items.map((ipa, idx) => {
                    const base = stripLengthMarks(ipa);
                    const off = VOWEL_OFFSET[base] || { dx: 0, dy: 0 };
                    const { ox, oy } = offsetToPx(off, W, H);

                    const x = ox + (start + idx) * spread;
                    const y = oy;

                    return (
                      <div
                        key={ipa}
                        className="absolute -translate-x-1/2 -translate-y-1/2"
                        style={{ left: x, top: y }}
                      >
                        <ChartSymbol
                          ipa={ipa}
                          overlap={getOverlap(ipa)}
                          selected={selectedIpa === ipa}
                          onClick={() => onSelect(ipa)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}